import React, { useMemo, useState } from 'react';
import { Box, Icon, makeStyles, MenuItem, MenuList, Popover, TextField, Chip } from '@material-ui/core';
import { RemoveCircleOutline } from '@material-ui/icons';
import SearchIcon from '@material-ui/icons/Search';
import Spinner from '@/se/components/Spinner';
import { useMutation, useQuery } from '@apollo/client';
import { list as listAnesthesiologists } from '../../../../../graph/anesthesiologists';
import { addAnesthesiologistShiftRoom } from '../../../../../graph/rooms';
import { removeAnesthesiologistShiftRoom } from '../../../../../graph/rooms';
import anesthesiaSvgIcon from '../../../../../assets/images/anesthesia.svg';
import { format } from 'date-fns';
import { Room } from '../../../../../types/Room';

const AnesthesiologistNameRoom = ({
  date,
  roomId,
  roomType,
  room,
  isKiosk,
  editableAnesthesiologist = false,
}: {
  date?: Date;
  roomId: number;
  roomType?: string;
  room?: Room;
  isKiosk?: boolean;
  editableAnesthesiologist?: boolean;
}) => {
  const classes = useStyles();
  const canEdit = !isKiosk && editableAnesthesiologist;

  const searchEl = React.useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = canEdit && Boolean(anchorEl);
  const [search, setSearch] = useState('');
  const popoverId = open ? 'simple-popover' : undefined;

  const { data, loading } = useQuery(listAnesthesiologists, { skip: !canEdit });
  const anesthesiologists = useMemo(() => data?.anesthesiologists || [], [data]);

  const filteredAnesthesiologists = useMemo(() => {
    const chunks = search.toLowerCase().split(/\s+/);
    return anesthesiologists.filter(a => chunks.every(chunk => a.name.toLowerCase().includes(chunk)));
  }, [search, anesthesiologists]);

  const [setRoomAnesthesiologist] = useMutation(addAnesthesiologistShiftRoom);
  const [removeRoomAnesthesiologist] = useMutation(removeAnesthesiologistShiftRoom, {
    update(cache, _result, { variables }) {
      const shiftId = variables?.id;
      if (shiftId == null) return;
      const cacheId = cache.identify({ __typename: 'AnesthesiologistShiftRoom', id: shiftId });
      if (cacheId) {
        cache.evict({ id: cacheId });
        cache.gc();
      }
    },
  });

  const openEditor = e => {
    if (!canEdit) return;
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSearchUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
  };

  const selectAnesthesiologist = async anesthesiologist => {
    if (!canEdit) return;

    try {
      date &&
        (await setRoomAnesthesiologist({
          variables: {
            id: roomId,
            date: format(date, 'YYYY-MM-DD'),
            anesthesiologistId: anesthesiologist.id,
            roomTypeString: roomType,
          },
        }));
      handleClose();
    } catch (e) {
      console.log(e);
    }
  };

  const removeSelectedAnesthesiologist = async anesthesiologist => {
    if (!canEdit) return;

    try {
      date &&
        (await removeRoomAnesthesiologist({
          variables: { id: anesthesiologist.id, roomTypeString: roomType },
        }));
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <>
      <Box display="flex" alignItems="center">
        {canEdit && (
          <Box onClick={openEditor} className={classes.addIcon}>
            <Icon>
              <img src={anesthesiaSvgIcon} alt="Add Anesthesiologist" width={22} height={22} />
            </Icon>
          </Box>
        )}

        {room?.anesthesiologistShifts?.map(a => (
          <Chip
            key={a.id}
            label={a?.anesthesiologist.name.split(' ').slice(-1)}
            onClick={canEdit ? () => removeSelectedAnesthesiologist(a) : undefined}
            onDelete={canEdit ? () => removeSelectedAnesthesiologist(a) : undefined}
            deleteIcon={canEdit ? <RemoveCircleOutline className={classes.iconRemove} /> : undefined}
          />
        ))}
      </Box>

      {open && (
        <Popover
          id={popoverId}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          {loading ? (
            <Box className={classes.spinnerContainer}>
              <Spinner freezeAnimation={!open} />
            </Box>
          ) : (
            <Box className={classes.root}>
              <TextField
                size="small"
                variant="outlined"
                value={search}
                onChange={handleSearchUpdate}
                inputRef={searchEl}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" className={classes.searchIcon} />,
                }}
                className={classes.textFieldRoot}
              />
              <MenuList className={classes.menu}>
                {filteredAnesthesiologists.map(a => (
                  <MenuItem key={a.id} onClick={() => selectAnesthesiologist(a)}>
                    {a.name}
                  </MenuItem>
                ))}
              </MenuList>
            </Box>
          )}
        </Popover>
      )}
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: { maxHeight: '38rem', overflow: 'hidden' },
  menu: { maxHeight: '28rem', overflowY: 'scroll' },
  textFieldRoot: { margin: '1rem', marginBottom: 0 },
  searchIcon: { marginLeft: '0.25em' },
  spinnerContainer: { padding: '1rem' },
  addIcon: { cursor: 'pointer' },
  iconRemove: { color: 'red' },
}));

export default AnesthesiologistNameRoom;
